import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProviders } from '../../../hooks/useProviders';
import {
  useCreateConsolidatedSupplyOrder,
  usePreviewConsolidatedSupplyOrder,
} from '../../../hooks/useConsolidatedSupplyOrders';
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage';
import { CONSOLIDATED_SUPPLY_TEXTS as TEXTS } from '../constants/consolidatedSupplyTexts';

/** Selección entre páginas y preparación explícita con revisión de cantidades por origen. */
export const useConsolidatedPurchases = (filters, searchTerm, orders) => {
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [provider, setProvider] = useState('');
  const [commitment, setCommitment] = useState('');
  const [requestId, setRequestId] = useState('');
  const [error, setError] = useState('');
  const [created, setCreated] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [previewReady, setPreviewReady] = useState(false);
  const previewSequence = useRef(0);
  const preview = usePreviewConsolidatedSupplyOrder();
  const create = useCreateConsolidatedSupplyOrder();
  const providers = useProviders({ enabled: open });
  const navigate = useNavigate();
  useEffect(() => {
    setSelected([]);
  }, [filters, searchTerm]);
  useEffect(() => {
    const unavailable = new Set(
      orders.filter((row) => row.has_purchasable_shortage !== true).map((row) => row.uuid)
    );
    setSelected((current) => {
      const next = current.filter((id) => !unavailable.has(id));
      return next.length === current.length ? current : next;
    });
  }, [orders]);

  const canSelect = (row) => row.has_purchasable_shortage === true;
  const toggle = (id) =>
    setSelected((current) => {
      if (current.includes(id)) return current.filter((value) => value !== id);
      return orders.some((row) => row.uuid === id && canSelect(row)) ? [...current, id] : current;
    });
  const togglePage = (data) => {
    const ids = data.filter(canSelect).map((row) => row.uuid);
    setSelected((current) =>
      ids.every((id) => current.includes(id))
        ? current.filter((id) => !ids.includes(id))
        : [...new Set([...current, ...ids])]
    );
  };
  const refresh = async () => {
    const sequence = ++previewSequence.current;
    setError('');
    setRequestId('');
    setPreviewReady(false);
    try {
      const data = await preview.mutateAsync({ external_order_ids: selected });
      if (sequence !== previewSequence.current) return;
      setRows(data.items.map((row) => ({ ...row, quantity: String(row.available_quantity) })));
      setExcluded(data.excluded_orders);
      setRequestId(crypto.randomUUID());
      setPreviewReady(true);
    } catch (failure) {
      if (sequence === previewSequence.current) {
        setError(getApiErrorMessage(failure, TEXTS.error));
      }
    }
  };
  const show = () => {
    setOpen(true);
    setCreated([]);
    setRows([]);
    setExcluded([]);
    setProvider('');
    setCommitment('');
    setSelectedCount(selected.length);
    refresh();
  };
  const changeQuantity = (id, value) => {
    setRows((current) =>
      current.map((row) =>
        row.uuid_external_order_item === id ? { ...row, quantity: value } : row
      )
    );
    setRequestId(crypto.randomUUID());
  };
  const save = async () => {
    setError('');
    try {
      const data = await create.mutateAsync({
        external_order_ids: selected,
        uuid_provider_legal_entity: provider,
        request_id: requestId,
        provider_delivery_commitment_at: commitment ? `${commitment}T12:00:00` : null,
        items: rows
          .filter((row) => Number(row.quantity) > 0)
          .map((row) => ({
            uuid_external_order_item: row.uuid_external_order_item,
            quantity: Number(row.quantity),
          })),
      });
      setCreated(data);
      setSelected([]);
    } catch (failure) {
      setError(getApiErrorMessage(failure, TEXTS.error));
    }
  };
  const chosen = rows.filter((row) => Number(row.quantity) > 0);
  const documentCount = new Set(chosen.map((row) => row.uuid_destination_warehouse)).size;
  const validQuantities = rows.every(
    (row) =>
      Number.isFinite(Number(row.quantity || 0)) &&
      Number(row.quantity || 0) >= 0 &&
      Number(row.quantity || 0) <= row.available_quantity
  );
  const saveHint =
    !previewReady || providers.error || !requestId
      ? TEXTS.retryRequired
      : !provider
        ? TEXTS.providerRequired
        : !chosen.length
          ? TEXTS.nothingToBuy
          : !validQuantities
            ? TEXTS.invalidQuantities
            : '';
  return {
    selected,
    canSelect,
    toggle,
    togglePage,
    show,
    clear: () => setSelected([]),
    drawerProps: {
      open,
      rows,
      excluded,
      provider,
      commitment,
      selectedCount,
      providers: providers.data || [],
      onProvider: (value) => {
        setProvider(value);
        setRequestId(crypto.randomUUID());
      },
      onCommitment: (value) => {
        setCommitment(value);
        setRequestId(crypto.randomUUID());
      },
      onQuantity: changeQuantity,
      onRefresh: () => {
        providers.refetch();
        refresh();
      },
      onSave: save,
      onClose: () => {
        if (create.isPending) return;
        previewSequence.current += 1;
        setOpen(false);
      },
      onView: () => {
        setOpen(false);
        navigate('/stock-compras/ordenes-proveedor');
      },
      isLoading: preview.isPending || providers.isLoading,
      isSaving: create.isPending,
      error: error || (providers.error ? TEXTS.providerError : ''),
      created,
      documentCount,
      saveHint,
      canSave: !saveHint,
    },
  };
};
